import { useMemo } from 'react';
import DOMPurify from 'isomorphic-dompurify';

let blankTargetRelHookInstalled = false;

function installBlankTargetRelHook(): void {
  if (blankTargetRelHookInstalled) {
    return;
  }
  blankTargetRelHookInstalled = true;
  DOMPurify.addHook('afterSanitizeAttributes', node => {
    if (node.nodeType !== 1 /* ELEMENT_NODE */) {
      return;
    }
    const el = node as Element;
    if (el.tagName !== 'A') {
      return;
    }
    if (el.getAttribute('target')?.trim() !== '_blank') {
      return;
    }
    el.setAttribute('rel', 'noopener noreferrer');
  });
}

installBlankTargetRelHook();

/**
 * Result returned from the useSanitize hook
 */
export interface UseSanitizeResult {
  /**
   * Whether the input contained HTML that was sanitized/modified.
   * True if HTML tags were found and processed, false for plain text.
   */
  isDangerouslySet: boolean;
  /**
   * The sanitized HTML string (if isDangerouslySet is true) or
   * the plain text content (if isDangerouslySet is false).
   */
  result: string;
}

/**
 * Custom hook for sanitizing rich text/HTML content using DOMPurify.
 *
 * This hook memoizes the sanitization process to avoid unnecessary re-computation
 * and determines whether the input contains HTML that requires dangerouslySetInnerHTML
 * or can be rendered as plain text.
 *
 * Sanitization uses `RETURN_DOM: true`. A one-time DOMPurify `afterSanitizeAttributes` hook
 * sets `rel="noopener noreferrer"` on `<a target="_blank">` to mitigate reverse tabnabbing.
 *
 * @param value - The HTML string or plain text to sanitize
 * @param config - DOMPurify configuration options (e.g., ALLOWED_TAGS, ALLOWED_ATTR, etc.)
 * @returns An object containing the sanitized result and a flag indicating if HTML was processed
 *
 * @example
 * ```typescript
 * // Sanitize with custom allowed tags
 * const { isDangerouslySet, result } = useSanitize(
 *   '<p>Hello <strong>world</strong></p>',
 *   { ALLOWED_TAGS: ['p', 'strong'] }
 * );
 *
 * // Render based on whether HTML was found
 * return isDangerouslySet
 *   ? <div dangerouslySetInnerHTML={{ __html: result }} />
 *   : <div>{result}</div>;
 * ```
 */
export function useSanitize(value: string | null | undefined, config?: DOMPurify.Config): UseSanitizeResult {
  return useMemo(() => {
    const inputValue = value?.trim() || '';

    // Build DOMPurify config, merging with provided config
    const purifyConfig: DOMPurify.Config = {
      ...config,
      RETURN_DOM: true,
    };

    // Sanitize the input
    const cleanEl = DOMPurify.sanitize(inputValue, purifyConfig) as unknown as HTMLElement;

    // Determine if the content contains HTML by checking if sanitization modified it
    const isDangerouslySet = cleanEl.textContent !== inputValue;

    // Return innerHTML if HTML was found, otherwise return plain text
    return {
      isDangerouslySet,
      result: (isDangerouslySet ? cleanEl.innerHTML : cleanEl.textContent) ?? '',
    };
  }, [value, config]);
}
